import React from 'react';
import { motion } from 'framer-motion';
import { FiAlertTriangle, FiMail } from 'react-icons/fi';

const RiskDisclaimer = () => {
  const sections = [
    {
      id: 1,
      title: "No Financial Licensing",
      content: (
        <>
          <p className="text-gray-400 mb-4">
            SnapChart AI and all affiliated brands (collectively, the "Service"), are not licensed, registered, or authorized to provide financial, investment, or trading advice in any jurisdiction.
          </p>
          <p className="text-cyan-400 text-sm">
            The Service does not offer brokerage, investment advisory services, portfolio management, or any regulated financial activity.
          </p>
        </>
      )
    },
    {
      id: 2,
      title: "Informational Purposes Only",
      content: (
        <>
          <p className="text-gray-400 mb-3">2.1 The Service provides AI-powered analytics, charting tools, insights, performance indicators, and discipline-based evaluations solely for informational and educational purposes.</p>
          <p className="text-gray-400 mb-3">2.2 All content generated by the Service, including AI predictions, signals, suggested setups, pattern detection, and behavioral analysis, is not a substitute for professional financial advice.</p>
          <p className="text-gray-400">2.3 Users are solely responsible for evaluating the suitability, accuracy, and relevance of any information before making financial decisions.</p>
        </>
      )
    },
    {
      id: 3,
      title: "Trading and Investment Risks",
      content: (
        <>
          <p className="text-gray-400 mb-3">3.1 Trading and investing in financial instruments—including stocks, options, futures, foreign exchange, derivatives, and cryptocurrencies—involves substantial risk of loss. Users may lose part or all of their invested capital.</p>
          <p className="text-gray-400 mb-3">3.2 Past performance, historical signals, AI model behavior, or simulated outcomes do not guarantee future results.</p>
          <p className="text-gray-400">3.3 Users must carefully assess their personal financial circumstances, experience level, and risk tolerance before using the Service.</p>
        </>
      )
    },
    {
      id: 4,
      title: "Proprietary Trading Disclaimer",
      content: (
        <>
          <p className="text-gray-400 mb-3">4.1 Any proprietary trading executed by SnapChart AI is done only with investor authorization, where applicable. Investors always retain custody of their own funds through regulated brokers or custodians.</p>
          <p className="text-gray-400 mb-3">4.2 SnapChart AI does not manage user funds, does not hold custody, and does not execute trades on behalf of users unless explicitly authorized through separate legal agreements.</p>
          <p className="text-gray-400 mb-3">4.3 Users who trade their own funds using the Service do so entirely at their own risk.</p>
          <p className="text-cyan-400 text-sm">4.4 SnapChart AI shall not be liable for any financial losses, damages, or negative outcomes resulting from self-directed trading or reliance on Service-generated insights.</p>
        </>
      )
    },
    {
      id: 5,
      title: "Sanctioned Countries and Restricted Jurisdictions",
      content: (
        <>
          <p className="text-gray-400 mb-4">5.1 The Service is not available to persons or entities located in, or operating from, restricted or sanctioned jurisdictions, including regions sanctioned by:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4 mb-4">
            <li>United Nations (UN)</li>
            <li>Office of Foreign Assets Control (OFAC – U.S.)</li>
            <li>European Union (EU)</li>
            <li>Monetary Authority of Singapore (MAS)</li>
            <li>Any other applicable regulatory authority</li>
          </ul>
          <p className="text-gray-400 mb-2">5.2 The Service operates from:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Singapore – for intellectual property ownership, corporate governance, and operational matters</li>
          </ul>
        </>
      )
    },
    {
      id: 6,
      title: "No Liability",
      content: (
        <>
          <p className="text-gray-400 mb-4">6.1 To the maximum extent permitted by law, SnapChart AI and all affiliates, directors, officers, employees, agents, and licensors disclaim liability for any direct, indirect, incidental, consequential, or punitive damages arising from:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4 mb-4">
            <li>Use or inability to use the Service</li>
            <li>Reliance on AI-generated signals, predictions, insights, or analytics</li>
            <li>Losses resulting from trading or investment activity</li>
            <li>Any errors, delays, omissions, or inaccuracies in Service output</li>
          </ul>
          <p className="text-gray-400 mb-3">6.2 Under no circumstances shall SnapChart AI be responsible for trading losses incurred by users trading their own funds.</p>
          <div className="p-4 bg-gradient-to-r from-red-600/10 to-orange-600/10 border border-red-500/20 rounded-xl">
            <p className="text-orange-300 font-medium">6.3 By using the Service, users fully assume all risks associated with financial markets and acknowledge that SnapChart AI provides no guarantees of profit.</p>
          </div>
        </>
      )
    },
    {
      id: 7,
      title: "User Responsibility",
      content: (
        <>
          <p className="text-gray-400 mb-3">7.1 Users are responsible for all trading decisions and for complying with local financial regulations applicable in their jurisdiction.</p>
          <p className="text-gray-400 mb-3">7.2 Users agree not to hold SnapChart AI liable for any financial, personal, legal, regulatory, or tax consequences resulting from their use of the Service or reliance on its data.</p>
          <p className="text-cyan-400 text-sm">7.3 Users acknowledge that self-directed trading based on Service insights is entirely at their own risk.</p>
        </>
      )
    },
    {
      id: 8,
      title: "External Links",
      content: (
        <p className="text-gray-400">The Service may include links to external third-party websites, brokers, APIs, or resources. SnapChart AI is not responsible for the content, accuracy, security, or practices of these third-party sites.</p>
      )
    },
    {
      id: 9,
      title: "Acceptance of Risk",
      content: (
        <>
          <p className="text-gray-400 mb-4">By accessing or using the Service, users acknowledge and agree that:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>They understand the inherent risks of trading and investing.</li>
            <li>They are solely responsible for all actions taken based on Service information.</li>
            <li>SnapChart AI provides no financial, investment, or trading advice.</li>
            <li>They are not residents or citizens of sanctioned or restricted jurisdictions.</li>
            <li>Any trading or investment activity conducted using the Service is at their sole and exclusive risk.</li>
          </ul>
        </>
      )
    },
    {
      id: 10,
      title: "Updates to This Disclaimer",
      content: (
        <p className="text-gray-400">SnapChart AI may update this Risk Disclaimer at any time. Continued use of the Service constitutes acceptance of the updated version.</p>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-cyan-600/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-r from-purple-600/20 to-cyan-600/20 blur-3xl rounded-full"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                <FiAlertTriangle className="text-3xl text-cyan-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                RISK DISCLAIMER
              </span>
            </h1>
            <p className="text-gray-400 text-sm">
              Effective Date: December 6, 2025
            </p>
          </motion.div>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-2xl"
        >
          <h2 className="text-xl font-bold text-white mb-4">Contact — SnapChart AI / TitanVue AI</h2>
          <div className="p-4 bg-gray-800/30 rounded-xl border border-white/5">
            <p className="text-cyan-400 font-medium">SnapChart AI</p>
            <a href="mailto:support@snap-chart.ai" className="text-gray-400 text-sm hover:text-cyan-300">support@snap-chart.ai</a>
          </div>
        </motion.div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="p-6 bg-gray-800/30 border border-white/5 rounded-2xl hover:border-purple-500/20 transition-colors"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 text-sm font-bold">
                  {section.id}
                </span>
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  {section.title}
                </span>
              </h3>
              <div className="pl-11">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-2xl text-center"
        >
          <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Contact Information
          </h3>
          <p className="text-gray-400 mb-4">For questions regarding this Risk Disclaimer, contact:</p>
          <a href="mailto:support@snap-chart.ai" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-xl text-cyan-400 hover:text-cyan-300 hover:border-purple-500/50 transition-all font-medium">
            <FiMail className="text-lg" />
            support@snap-chart.ai
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default RiskDisclaimer;
